import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileSpreadsheet, Copy, Loader2, Check, Send, Globe, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const itemBullets = (items) => (items || []).map((i) => {
  const qn = i.quantity != null && i.stock_qty ? Math.max(0, i.quantity - i.stock_qty) : i.quantity;
  const q = qn != null ? ` — ${qn}${i.unit ? " " + i.unit : ""}` : "";
  const s = i.spec_or_part ? `, ${i.spec_or_part}` : "";
  return `${i.description}${s}${q}`;
}).join("\n");

const CHECKLIST =
  "Kindly include the following in your quotation:\n" +
  "Item price\nLead time\nWeight and dimensions\nIncoterms and HS Codes\nProduct datasheets (if available)";
const COMPANY_INTRO =
  "We are Somcore Group Limited, an industrial supply and procurement company based in Tanzania serving clients across the mining and oil and gas sector. ";


export default function SendToSuppliers({ rfqId, open, onOpenChange, onSent }) {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ due_by: "", currency: "USD", incoterms: "", delivery_date: "", delivery_location: "", issued_by_id: "" });
  const flashCls = (empty) => (empty ? "ring-2 ring-amber-400 animate-pulse" : "");
  const [downloading, setDownloading] = useState("");
  const [marking, setMarking] = useState("");
  const [sentMap, setSentMap] = useState({});
  const [openSentOverride, setOpenSentOverride] = useState(null);
  const openSent = openSentOverride ?? !!data?.open_sent;

  const markOpenSent = async () => {
    const next = !openSent;
    setMarking("__open__");
    setOpenSentOverride(next);
    try {
      await api.post(`/rfqs/${rfqId}/supplier-sent`, { supplier_id: "__open__", sent: next });
      load(); onSent && onSent();
      toast.success(next ? "General RFQ marked as sent" : "General RFQ unmarked");
    } catch (e) { setOpenSentOverride(!next); toast.error(apiError(e)); } finally { setMarking(""); }
  };
  const [copied, setCopied] = useState("");
  const [expanded, setExpanded] = useState("");
  const [tplStyle, setTplStyle] = useState({}); // supplier_id -> 'recurring'|'new'
  const [newMode, setNewMode] = useState(""); // '' (choose) | 'inline' | 'attached'
  const [recipientSel, setRecipientSel] = useState({}); // supplier_id -> 'div:{cat}' | 'c:{email}' | ''

  const firstOf = (name) => (name || "").trim().split(" ")[0];
  const resolveRecipients = (s) => {
    const sel = recipientSel[s.supplier_id];
    const alwaysCc = s.always_cc || [];
    const dedupe = (list, toEmail) => {
      const seen = new Set(toEmail ? [toEmail.toLowerCase()] : []);
      const out = [];
      for (const c of list) { const e = (c.email || "").toLowerCase(); if (e && !seen.has(e)) { out.push(c); seen.add(e); } }
      return out;
    };
    if (sel && sel.startsWith("div:")) {
      const cat = sel.slice(4);
      const d = (s.division_options || []).find((x) => x.category === cat);
      if (d) {
        const toC = (s.contacts_options || []).find((c) => c.email === d.to_email) || {};
        return { toEmail: d.to_email, toFirst: toC.first_name || firstOf(toC.name), cc: dedupe([...(d.cc || []), ...alwaysCc], d.to_email) };
      }
    }
    if (sel && sel.startsWith("c:")) {
      const em = sel.slice(2);
      const c = (s.contacts_options || []).find((x) => x.email === em) || { email: em };
      return { toEmail: em, toFirst: c.first_name || firstOf(c.name), cc: dedupe(alwaysCc, em) };
    }
    return { toEmail: s.email, toFirst: s.attention_first || firstOf(s.attention), cc: dedupe([...(s.cc || []), ...alwaysCc], s.email) };
  };

  const load = useCallback(() => {
    api.get(`/rfqs/${rfqId}/supplier-send-preview`).then((r) => {
      setData(r.data);
      setForm((f) => ({ ...f, delivery_location: r.data.delivery_location || "" }));
    }).catch((e) => toast.error(apiError(e)));
  }, [rfqId]);

  useEffect(() => { if (open) { load(); api.get("/team-members").then((r) => setMembers(r.data)).catch(() => {}); } }, [open, load]);

  const issuer = members.find((m) => m.id === form.issued_by_id);
  const issuerName = issuer?.full_name || issuer?.name || data?.issuer_name || "";

  const subjectFor = (s) => {
    const ref = data?.rfq_ref || data?.rfq_title || data?.project_ref || "RFQ";
    const mid = s?.is_oem ? (data?.client_name || "") : (s?.name || "");
    return [ref, mid, "Somcore Group Tanzania"].filter(Boolean).join(" | ");
  };

  // OEM body references the end customer and (if any) the end user/project.
  const oemBody = (items, greetName) => {
    const client = data?.client_name || "one of our clients";
    const endUser = data?.end_user ? ` for the end user / project ${data.end_user}` : "";
    const ref = data?.rfq_ref || "";
    const reqLine = newMode === "attached"
      ? `We have received an enquiry from our client ${client}${endUser}. Please find attached our Somcore RFQ${ref ? ` ${ref}` : ""} with the full specifications and kindly share your best quotation.`
      : `We have received an enquiry from our client ${client}${endUser} under our Somcore RFQ${ref ? ` ${ref}` : ""}, and would like to request your best quotation for the items below.\n\n${itemBullets(items)}`;
    return `Hi ${greetName || "Team"},\n\n` +
      `Hope you are well.\n\n` +
      `${reqLine}\n\n` +
      `${CHECKLIST}\n\n` +
      `Thank you and looking forward to your positive response.`;
  };

  // style: 'recurring' | 'new' ; mode: 'inline' | 'attached'
  const supplierBody = (style, items, greetName) => {
    const intro = style === "new" ? COMPANY_INTRO : "";
    const ref = data?.rfq_ref || "";
    const reqLine = newMode === "attached"
      ? `${intro}One of our clients has a requirement for the items listed in the attached Somcore RFQ${ref ? ` ${ref}` : ""} for the full specifications.`
      : `${intro}One of our clients has a requirement for the below items under our Somcore RFQ${ref ? ` ${ref}` : ""}, kindly review and share your quotation accordingly.\n\n${itemBullets(items)}`;
    const closing = style === "new" ? "Thank you and looking forward to your positive response." : "Thank you.";
    return `Hi ${greetName || "Supplier Team"},\n\n` +
      `Hope you are well.\n\n` +
      `${reqLine}\n\n` +
      `${CHECKLIST}\n\n` +
      `Please let us know if you require anything further from our end and we will be happy to assist.\n\n` +
      `${closing}`;
  };

  const emailText = (s) => {
    const style = tplStyle[s.supplier_id] || "recurring";
    const r = resolveRecipients(s);
    const greet = r.toFirst || s.name || "Supplier Team";
    const body = s.is_oem ? oemBody(s.items, greet) : supplierBody(style, s.items, greet);
    const ccLine = (r.cc && r.cc.length) ? `\nCc: ${r.cc.map((c) => c.email).filter(Boolean).join(", ")}` : "";
    return `To: ${r.toEmail || ""}${ccLine}\nSubject: ${subjectFor(s)}\n\n${body}`;
  };

  const copy = async (key, text) => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text); ok = true;
      }
    } catch { ok = false; }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        ok = document.execCommand("copy"); ta.remove();
      } catch { ok = false; }
    }
    if (ok) { setCopied(key); setTimeout(() => setCopied(""), 1800); toast.success("Email copied to clipboard"); }
    else { toast.message("Select the email preview below and copy it manually"); }
  };

  const downloadBlob = async (key, payload, filename) => {
    setDownloading(key);
    try {
      const res = await api.post(`/rfqs/${rfqId}/supplier-rfq/export`, payload, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : filename;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      if (payload.mark_sent) { load(); onSent && onSent(); }
    } catch (e) { toast.error(apiError(e)); } finally { setDownloading(""); }
  };

  const markSent = async (s, sent) => {
    setMarking(s.supplier_id);
    setSentMap((m) => ({ ...m, [s.supplier_id]: sent }));
    try {
      await api.post(`/rfqs/${rfqId}/supplier-sent`, { supplier_id: s.supplier_id, sent });
      load(); onSent && onSent();
      toast.success(sent ? `Marked as sent to ${s.name}` : `Unmarked ${s.name}`);
    } catch (e) { setSentMap((m) => ({ ...m, [s.supplier_id]: !sent })); toast.error(apiError(e)); } finally { setMarking(""); }
  };

  const basePayload = () => ({
    due_by: form.due_by, currency: form.currency, incoterms: form.incoterms,
    delivery_date: form.delivery_date, delivery_location: form.delivery_location,
    issued_by: issuerName,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="send-suppliers-dialog" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Send RFQ to Suppliers</DialogTitle></DialogHeader>
        {!data ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
          <div className="space-y-5">
            <p className="text-sm text-muted-foreground">
              RFQ Ref <span className="font-mono text-[#1B4383]">{data.rfq_ref}</span>. Fill the shared details, then for each supplier choose <b>Recurring or New</b>, open the email draft to copy into your inbox, download the Excel if needed, and tick <b>RFQ Sent to Supplier?</b> once done.
            </p>

            <div className="grid gap-3 rounded-lg border border-border bg-slate-50/60 p-4 sm:grid-cols-3">
              <div className="space-y-1"><Label className="text-xs">Quotation Due By</Label>
                <Input type="date" className={`h-9 ${flashCls(!form.due_by)}`} value={form.due_by} onChange={(e) => setForm({ ...form, due_by: e.target.value })} data-testid="send-due-by" /></div>
              <div className="space-y-1"><Label className="text-xs">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="h-9" data-testid="send-currency"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="TZS">TZS</SelectItem><SelectItem value="TZS / USD">TZS / USD</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">Required Incoterms</Label>
                <Input className={`h-9 ${flashCls(!form.incoterms)}`} placeholder="CIF Dar es Salaam" value={form.incoterms} onChange={(e) => setForm({ ...form, incoterms: e.target.value })} data-testid="send-incoterms" /></div>
              <div className="space-y-1"><Label className="text-xs">Required Delivery Date</Label>
                <Input type="date" className={`h-9 ${flashCls(!form.delivery_date)}`} value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} data-testid="send-delivery-date" /></div>
              <div className="space-y-1"><Label className="text-xs">Delivery Location</Label>
                <Input className={`h-9 ${flashCls(!form.delivery_location)}`} value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} data-testid="send-delivery-location" /></div>
              <div className="space-y-1"><Label className="text-xs">Issued By</Label>
                <Select value={form.issued_by_id || "none"} onValueChange={(v) => setForm({ ...form, issued_by_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="send-issued-by"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— (me)</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>

            <Tabs defaultValue="direct">
              <TabsList data-testid="send-mode-tabs">
                <TabsTrigger value="direct" data-testid="send-tab-direct">Direct (Per Supplier)</TabsTrigger>
                <TabsTrigger value="open" data-testid="send-tab-open">General / Open</TabsTrigger>
              </TabsList>

              <TabsContent value="direct" className="space-y-3 pt-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="direct-newmode-row">
                  <span>For "New Supplier" Emails, List Items:</span>
                  <Select value={newMode || undefined} onValueChange={setNewMode}>
                    <SelectTrigger className={`h-7 w-[210px] text-xs ${!newMode ? "ring-2 ring-amber-400 animate-pulse" : ""}`} data-testid="new-mode-select-direct"><SelectValue placeholder="Choose How To List Items" /></SelectTrigger>
                    <SelectContent><SelectItem value="inline">Write Items In The Email</SelectItem><SelectItem value="attached">Refer To Attached RFQ</SelectItem></SelectContent>
                  </Select>
                </div>
                {!data.suppliers.length && <p className="py-6 text-center text-sm text-muted-foreground">No suppliers assigned yet. Assign suppliers to items first.</p>}
                {data.suppliers.map((s) => {
                  const isOpen = expanded === s.supplier_id;
                  const isSent = sentMap[s.supplier_id] ?? s.sent;
                  return (
                    <div key={s.supplier_id} className="rounded-lg border border-border" data-testid={`send-supplier-${s.supplier_id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3 p-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{s.name}</span>
                            {isSent && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700" data-testid={`sent-badge-${s.supplier_id}`}>Sent</span>}
                          </div>
                          <div className="text-xs text-muted-foreground">{s.items.length} item(s) · {resolveRecipients(s).toEmail || "no email on file"}</div>
                          {s.is_oem && !s.auto_matched && (
                            <div className="mt-0.5 text-xs text-amber-600" data-testid={`no-division-hint-${s.supplier_id}`}>
                              No division matched these items — pick who to send to.
                            </div>
                          )}
                          {s.division_labels && s.division_labels.length > 0 && (
                            <div className="mt-0.5 text-xs text-[#1B4383]" data-testid={`division-hint-${s.supplier_id}`}>
                              Division: {s.division_labels.join(", ")}
                            </div>
                          )}
                          {(s.division_options?.length > 0 || s.contacts_options?.length > 0) && (
                            <select
                              value={recipientSel[s.supplier_id] || ""}
                              onChange={(e) => setRecipientSel({ ...recipientSel, [s.supplier_id]: e.target.value })}
                              className="mt-1.5 rounded-md border border-input bg-white px-2 py-1 text-xs"
                              data-testid={`recipient-select-${s.supplier_id}`}
                            >
                              <option value="">Send to: {s.auto_matched ? "auto (division)" : "auto (primary contact)"}</option>
                              {(s.division_options || []).map((d) => (
                                <option key={`div-${d.category}`} value={`div:${d.category}`}>Division: {d.label}</option>
                              ))}
                              {(s.contacts_options || []).map((c) => (
                                <option key={`c-${c.email}`} value={`c:${c.email}`}>{c.name || c.email}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Select value={tplStyle[s.supplier_id] || undefined} onValueChange={(v) => setTplStyle({ ...tplStyle, [s.supplier_id]: v })}>
                            <SelectTrigger className={`h-8 w-[170px] text-xs ${!tplStyle[s.supplier_id] ? "ring-2 ring-amber-400 animate-pulse" : ""}`} data-testid={`tpl-style-${s.supplier_id}`}><SelectValue placeholder="Recurring or New?" /></SelectTrigger>
                            <SelectContent><SelectItem value="recurring">Recurring supplier</SelectItem><SelectItem value="new">New supplier</SelectItem></SelectContent>
                          </Select>
                          <Button variant="outline" size="sm" className="h-8" onClick={() => setExpanded(isOpen ? "" : s.supplier_id)} data-testid={`toggle-email-${s.supplier_id}`}>
                            {isOpen ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}Open Email Draft Below
                          </Button>
                          <Button variant="outline" size="sm" className="h-8"
                            onClick={() => downloadBlob(`dx-${s.supplier_id}`, { ...basePayload(), mode: "direct", supplier_id: s.supplier_id, format: "excel", mark_sent: false }, `${data.rfq_ref}_${s.name}.xlsx`)}
                            disabled={downloading === `dx-${s.supplier_id}`} data-testid={`download-excel-${s.supplier_id}`}>
                            {downloading === `dx-${s.supplier_id}` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />}Download Excel
                          </Button>
                          <Button size="sm" variant={isSent ? "outline" : "default"}
                            className={`h-8 ${isSent ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "bg-amber-500 text-white hover:bg-amber-600 ring-2 ring-amber-400 animate-pulse"}`}
                            onClick={() => markSent(s, !isSent)} disabled={marking === s.supplier_id} data-testid={`mark-sent-${s.supplier_id}`}>
                            {marking === s.supplier_id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : isSent ? <Check className="mr-1 h-4 w-4" /> : null}
                            {isSent ? "RFQ Sent \u2713" : "RFQ Sent to Supplier?"}
                          </Button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="space-y-2 border-t border-border p-3">
                          <div className="flex justify-end">
                            <Button variant="outline" size="sm" className="h-8" onClick={() => copy(`d-${s.supplier_id}`, emailText(s))} data-testid={`copy-email-${s.supplier_id}`}>
                              {copied === `d-${s.supplier_id}` ? <Check className="mr-1 h-4 w-4 text-emerald-600" /> : <Copy className="mr-1 h-4 w-4" />}Copy email
                            </Button>
                          </div>
                          <Textarea rows={12} readOnly value={emailText(s)} className="font-mono text-xs" data-testid={`email-preview-${s.supplier_id}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </TabsContent>

              <TabsContent value="open" className="space-y-3 pt-3">
                <div className="rounded-lg border border-border p-4" data-testid="send-open-card">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-[#1B4383]" />
                      <div>
                        <div className="font-medium text-slate-900">General RFQ (broadcast)</div>
                        <div className="text-xs text-muted-foreground">All {data.all_items.length} line item(s), no named supplier — send to new/unknown suppliers.</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => copy("open", `Subject: ${subjectFor()}\n\n${supplierBody("new", data.all_items, "Supplier Team")}`)} data-testid="copy-open-email">
                        {copied === "open" ? <Check className="mr-1 h-4 w-4 text-emerald-600" /> : <Copy className="mr-1 h-4 w-4" />}Copy Email
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => downloadBlob("openx", { ...basePayload(), mode: "open", format: "excel" }, `${data.rfq_ref}_GENERAL.xlsx`)} disabled={downloading === "openx"} data-testid="download-open-excel">
                        {downloading === "openx" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />}Download Excel
                      </Button>
                      <Button size="sm" variant={openSent ? "outline" : "default"}
                        className={`${openSent ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "bg-amber-500 text-white hover:bg-amber-600 ring-2 ring-amber-400 animate-pulse"}`}
                        onClick={markOpenSent} disabled={marking === "__open__"} data-testid="mark-open-sent">
                        {marking === "__open__" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : openSent ? <Check className="mr-1 h-4 w-4" /> : null}
                        {openSent ? "RFQ Sent \u2713" : "RFQ Sent?"}
                      </Button>
                    </div>
                  </div>
                  <Textarea rows={12} readOnly value={`Subject: ${subjectFor()}\n\n${supplierBody("new", data.all_items, "Supplier Team")}`} className="font-mono text-xs" data-testid="open-email-preview" />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
